import { MagnifyingGlass, TrendUp, TrendDown, Minus, Pill, Lightning, Heart } from '@phosphor-icons/react';
import type { HealthAnalysisResult } from '../../stores/chatSession';

interface HealthAnalysisCardProps {
  analysis: HealthAnalysisResult;
}

const CONFIDENCE_COLORS: Record<string, string> = {
  strong: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  moderate: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  suggestive: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function InsightIcon({ type }: { type: string }) {
  switch (type) {
    case 'treatment_change_correlation':
      return <Pill size={14} weight="duotone" className="text-blue-500" />;
    case 'symptom_progression':
      return <TrendUp size={14} weight="duotone" className="text-orange-500" />;
    case 'symptom_cycle_correlation':
      return <Heart size={14} weight="duotone" className="text-pink-500" />;
    case 'severity_trend':
      return <TrendDown size={14} weight="duotone" className="text-purple-500" />;
    case 'lab_symptom_correlation':
      return <Lightning size={14} weight="duotone" className="text-amber-500" />;
    default:
      return <Minus size={14} className="text-gray-400" />;
  }
}

export default function HealthAnalysisCard({ analysis }: HealthAnalysisCardProps) {
  // Empty results: render a compact chip so the user sees the analysis ran.
  // Returning null here was the root cause of Romy narrating "I don't have
  // past data for X yet" in prose — the card was the receipt; without it,
  // the model filled the silence with a redundant explanation.
  if (analysis.insightCount === 0) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-400 not-prose">
        <MagnifyingGlass size={14} weight="duotone" className="text-blue-500" />
        <span>Checked your data on <strong className="text-gray-700 dark:text-gray-300">{analysis.symptom}</strong> — no patterns yet</span>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-gray-800 overflow-hidden not-prose">
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-blue-500" />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
            Data Analysis: {analysis.symptom}
          </span>
        </div>
        {analysis.hypothesis && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">
            Testing: {analysis.hypothesis}
          </p>
        )}
        <ul className="space-y-1.5">
          {analysis.insights.map((insight, i) => (
            <li key={i} className="flex items-start gap-2">
              <InsightIcon type={insight.type} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-100">
                    {insight.headline}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${CONFIDENCE_COLORS[insight.confidence] || CONFIDENCE_COLORS.suggestive}`}>
                    {insight.confidence}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
                  {insight.explanation}
                </p>
              </div>
            </li>
          ))}
        </ul>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
          {analysis.insightCount} pattern{analysis.insightCount !== 1 ? 's' : ''} found across your health data
        </p>
      </div>
    </div>
  );
}
